import dayjs from "dayjs";
import { createContext, ReactNode, useEffect, useMemo, useState } from "react";

import {
  IDateRangeFilter,
  IPortalPreferences,
  IStaffPreferences,
  IUserPreferences,
} from "../@types/Preferences";
import {
  AUTH_GRAPH_PREFERENCES,
  AUTH_PORTAL_PREFERENCES,
  AUTH_STAFF_PREFERENCES,
  AUTH_UNIT_PREFERENCES,
} from "../constants/localStorageKeys";
import {
  DATE_FILTER_ALL,
  DATE_FILTER_CUSTOM,
} from "../pages/PatientPage/components/MeasurementModal/components/CustomOptionsBar";
import { dateFilterOptions } from "../pages/PatientPage/components/MeasurementModal/components/CustomOptionsBar/dateFilterOptions";
import { safeStorage, safeJsonParse } from "../utils/storage";

type UserPreferencesContextData = {
  preferences: UserPreferences;
  changeUserPreferences: (userPreferences: UserPreferences) => void;
  portalPreferences: IPortalPreferences;
  changePortalPreferences: (portalPreferences: IPortalPreferences) => void;
  staffPreferences: IStaffPreferences;
  changeStaffPreferences: (staffPreferences: IStaffPreferences) => void;
  unitPreferences: IUserPreferences;
  changeUnitPreferences: (unitPreferences: IUserPreferences) => void;
  getDateRangeFilter: () => IDateRangeFilter;
};

type UserPreferencesProviderProps = {
  children: ReactNode;
};

type UserPreferences = {
  hideFvcRejected: boolean;
  hideFev1Rejected: boolean;
  hideFvcUsable: boolean;
  hideFev1Usable: boolean;
};

export const UserPreferencesContext = createContext(
  {} as UserPreferencesContextData
);

export function UserPreferencesProvider({
  children,
}: UserPreferencesProviderProps) {
  const defaultUserPreferences: UserPreferences = {
    hideFvcRejected: false,
    hideFev1Rejected: false,
    hideFvcUsable: false,
    hideFev1Usable: false,
  };

  const [userPreferences, setUserPreferences] = useState<UserPreferences>(
    () => {
      const userPreferencesStored = safeStorage.getItem(AUTH_GRAPH_PREFERENCES);
      return safeJsonParse<UserPreferences>(
        userPreferencesStored,
        defaultUserPreferences
      );
    }
  );

  const [portalPreferences, setPortalPreferences] =
    useState<IPortalPreferences>(() => {
      const storedPortalPreferences = safeStorage.getItem(
        AUTH_PORTAL_PREFERENCES
      );

      if (storedPortalPreferences) {
        const parsedPortalPreferences = safeJsonParse<IPortalPreferences>(
          storedPortalPreferences,
          {} as IPortalPreferences
        );
        return {
          ...parsedPortalPreferences,
          customFromDate: parsedPortalPreferences.customFromDate
            ? dayjs(parsedPortalPreferences.customFromDate)
            : undefined,
          customToDate: parsedPortalPreferences.customToDate
            ? dayjs(parsedPortalPreferences.customToDate)
            : undefined,
        };
      }

      return "" as unknown as IPortalPreferences;
    });

  const [staffPreferences, setStaffPreferences] = useState<IStaffPreferences>(
    () => {
      const storedStaffPreferences = safeStorage.getItem(
        AUTH_STAFF_PREFERENCES
      );
      if (storedStaffPreferences) {
        return safeJsonParse<IStaffPreferences>(
          storedStaffPreferences,
          {} as IStaffPreferences
        );
      }

      return "" as unknown as IStaffPreferences;
    }
  );

  const [unitPreferences, setUnitPreferences] = useState<IUserPreferences>(
    () => {
      const storedUnitPreferences = safeStorage.getItem(AUTH_UNIT_PREFERENCES);
      if (storedUnitPreferences) {
        return safeJsonParse<IUserPreferences>(
          storedUnitPreferences,
          {} as IUserPreferences
        );
      }

      return "" as unknown as IUserPreferences;
    }
  );

  useEffect(() => {
    safeStorage.setItem(
      AUTH_GRAPH_PREFERENCES,
      JSON.stringify(userPreferences)
    );
  }, [userPreferences]);

  useEffect(() => {
    safeStorage.setItem(
      AUTH_PORTAL_PREFERENCES,
      JSON.stringify(portalPreferences)
    );
  }, [portalPreferences]);

  useEffect(() => {
    safeStorage.setItem(
      AUTH_STAFF_PREFERENCES,
      JSON.stringify(staffPreferences)
    );
  }, [staffPreferences]);

  useEffect(() => {
    safeStorage.setItem(AUTH_UNIT_PREFERENCES, JSON.stringify(unitPreferences));
  }, [unitPreferences]);

  const handleOnChangeUserPreferences = (userPreferences: UserPreferences) => {
    setUserPreferences(userPreferences);
  };

  const handleOnChangePortalPreferences = (
    portalPreferences: IPortalPreferences
  ) => {
    setPortalPreferences(portalPreferences);
  };

  const handleOnChangeStaffPreferences = (
    staffPreferences: IStaffPreferences
  ) => {
    setStaffPreferences(staffPreferences);
  };

  const handleOnChangeUnitPreferences = (unitPreferences: IUserPreferences) => {
    setUnitPreferences({ ...unitPreferences });
  };

  const handleGetDateFilterRange = () => {
    const minDate = dayjs("1900-01-01T00:00:00Z");
    let endDate = dayjs();
    const dateFilterOption = dateFilterOptions.find(
      (df) => df.id === (portalPreferences.dateRangeFilter || DATE_FILTER_ALL)
    );
    let startDate = dateFilterOption?.value(endDate) ?? minDate;

    if (
      portalPreferences.dateRangeFilter === DATE_FILTER_CUSTOM &&
      portalPreferences.customFromDate &&
      portalPreferences.customToDate
    ) {
      startDate = dayjs.isDayjs(portalPreferences.customFromDate)
        ? portalPreferences.customFromDate
        : dayjs(portalPreferences.customFromDate);

      endDate = dayjs.isDayjs(portalPreferences.customToDate)
        ? portalPreferences.customToDate
        : dayjs(portalPreferences.customToDate);
    }

    if (!startDate.isValid() || startDate.isBefore(minDate)) {
      startDate = minDate;
    }

    const filter = {
      id: portalPreferences.dateRangeFilter || DATE_FILTER_ALL,
      startDate,
      endDate,
    };

    return filter;
  };

  const userPreferencesValues = useMemo(() => {
    return {
      preferences: userPreferences,
      changeUserPreferences: handleOnChangeUserPreferences,
      portalPreferences,
      changePortalPreferences: handleOnChangePortalPreferences,
      staffPreferences,
      changeStaffPreferences: handleOnChangeStaffPreferences,
      unitPreferences,
      changeUnitPreferences: handleOnChangeUnitPreferences,
      getDateRangeFilter: handleGetDateFilterRange,
    };
  }, [userPreferences, portalPreferences, staffPreferences, unitPreferences]);

  return (
    <UserPreferencesContext.Provider value={userPreferencesValues}>
      {children}
    </UserPreferencesContext.Provider>
  );
}
